import { useClient } from '@cotera/client/app/hooks/use-client';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useLogger } from '../actions/framework/logger';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { DollarSign, Clock, CheckCircle2, Users, Gift } from 'lucide-react';

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

const formatDate = (date: Date | string) => {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(new Date(date as string));
};

const getInitials = (name: string) => {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const StatCard: React.FC<{
  label: string;
  value: string;
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  subtitle?: string;
}> = ({ label, value, icon, iconBg, iconColor, subtitle }) => (
  <div className="rounded-lg border border-border p-4">
    <div className="flex items-center gap-3">
      <div
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${iconBg}`}
      >
        <div className={iconColor}>{icon}</div>
      </div>
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-lg font-semibold tracking-tight">{value}</p>
        {subtitle !== undefined && (
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        )}
      </div>
    </div>
  </div>
);

export const ReferralsSectionView: React.FC = () => {
  const { data: referralPaymentsRes } = useReferralPayments();
  const logger = useLogger();

  if (referralPaymentsRes.isErr()) {
    logger.error(
      `Error Fetching referral Payments (Referrals Settings): ${referralPaymentsRes.error.err.errorType}`
    );
    return (
      <div className="flex flex-col items-center gap-3 py-10 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-rose-50">
          <DollarSign className="h-6 w-6 text-rose-500" />
        </div>
        <div>
          <p className="text-sm font-medium">Unable to load referral data</p>
          <p className="text-sm text-muted-foreground mt-1">
            We're aware of this issue and working on a fix.
          </p>
        </div>
      </div>
    );
  }

  const { referralPayments } = referralPaymentsRes.value;

  if (referralPayments.length === 0) {
    return (
      <div className="flex flex-col items-center gap-4 py-12 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-indigo-50">
          <Gift className="h-7 w-7 text-indigo-500" />
        </div>
        <div>
          <p className="text-base font-semibold">No referrals yet</p>
          <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">
            When your referrals make payments, your commissions will appear
            here.
          </p>
        </div>
      </div>
    );
  }

  const totalCommission = referralPayments.reduce(
    (sum, p) => sum + p.commissionAmount,
    0
  );
  const totalDisbursed = referralPayments
    .filter((p) => p.commissionDisbursedAt !== null)
    .reduce((sum, p) => sum + p.commissionAmount, 0);
  const totalPending = totalCommission - totalDisbursed;
  const referredOrgs = Object.values(
    referralPayments.reduce<
      Record<string, { name: string; totalCommission: number }>
    >((acc, p) => {
      const key = p.referredOrg.name;
      if (acc[key] === undefined) {
        acc[key] = { name: key, totalCommission: 0 };
      }
      acc[key].totalCommission += p.commissionAmount;
      return acc;
    }, {})
  );
  const uniqueReferredUsers = new Set(
    referralPayments.map((p) => p.referredUser.email)
  ).size;

  return (
    <div className="flex flex-col gap-8">
      <div>
        <h3 className="font-semibold">Your Referrals</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Track your referral commissions and payouts.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          label="Total Earned"
          value={formatCurrency(totalCommission)}
          icon={<DollarSign className="h-4 w-4" />}
          iconBg="bg-indigo-50"
          iconColor="text-indigo-600"
        />
        <StatCard
          label="Paid Out"
          value={formatCurrency(totalDisbursed)}
          icon={<CheckCircle2 className="h-4 w-4" />}
          iconBg="bg-emerald-50"
          iconColor="text-emerald-600"
        />
        <StatCard
          label="Pending"
          value={formatCurrency(totalPending)}
          icon={<Clock className="h-4 w-4" />}
          iconBg="bg-amber-50"
          iconColor="text-amber-600"
        />
        <StatCard
          label="Referrals"
          value={String(uniqueReferredUsers)}
          icon={<Users className="h-4 w-4" />}
          iconBg="bg-purple-50"
          iconColor="text-purple-600"
          subtitle={`${referralPayments.length} payment${
            referralPayments.length === 1 ? '' : 's'
          }`}
        />
      </div>

      {referredOrgs.length > 0 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-2">
            Organizations
          </p>
          <div className="flex flex-wrap gap-2">
            {referredOrgs.map((org) => (
              <div
                key={org.name}
                className="flex items-center gap-2 rounded-full bg-muted/50 px-3 py-1.5"
              >
                <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-[10px] font-medium text-indigo-600">
                  {getInitials(org.name)}
                </div>
                <span className="text-sm">{org.name}</span>
                <span className="text-xs text-muted-foreground">
                  {formatCurrency(org.totalCommission)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <p className="text-sm font-medium mb-4">Commission History</p>
        <div className="rounded-lg border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Referred User</TableHead>
                <TableHead>Organization</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Payment</TableHead>
                <TableHead className="text-right">Commission</TableHead>
                <TableHead className="text-right">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {referralPayments.map((payment, idx) => (
                <TableRow key={idx}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium text-muted-foreground">
                        {getInitials(payment.referredUser.name)}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium truncate">
                          {payment.referredUser.name}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {payment.referredUser.email}
                        </p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{payment.referredOrg.name}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground">
                      {formatDate(payment.referredPaymentReceivedAt)}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="text-sm text-muted-foreground">
                      {formatCurrency(payment.referredPaymentAmount)}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="text-sm font-semibold">
                      {formatCurrency(payment.commissionAmount)}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    {payment.commissionDisbursedAt !== null ? (
                      <Badge variant="green">
                        Paid {formatDate(payment.commissionDisbursedAt)}
                      </Badge>
                    ) : (
                      <Badge variant="amber">Pending</Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

const useReferralPayments = () => {
  const client = useClient();

  return useSuspenseQuery({
    queryKey: ['referral-payments'],
    queryFn: async () => {
      const res = await client.t.referrals.getReferralPayments({});
      return res;
    },
  });
};
